import type { InferRpcDefinition } from "@redotech/rpc/definition";
import { adjustInventory } from "./schema/adjust-inventory";
import { cancelAdjustmentSession } from "./schema/adjustment-session/cancel-adjustment-session";
import { createAdjustmentSession } from "./schema/adjustment-session/create-adjustment-session";
import { getAdjustmentSession } from "./schema/adjustment-session/get-adjustment-session";
import { listAdjustmentSessions } from "./schema/adjustment-session/list-adjustment-sessions";
import { submitAdjustmentSession } from "./schema/adjustment-session/submit-adjustment-session";
import { updateAdjustmentSession } from "./schema/adjustment-session/update-adjustment-session";
import { createInventoryTransfer } from "./schema/create-inventory-transfer";
import { createMarketplaceListing } from "./schema/create-marketplace-listing";
import { deleteProduct } from "./schema/delete-product";
import { deleteProductFamilies } from "./schema/delete-product-families";
import { bulkUpdateForecastingFields } from "./schema/forecasting/bulk-update-forecasting-fields";
import { getForecastingProducts } from "./schema/forecasting/get-forecasting-products";
import { getProductVendors } from "./schema/forecasting/get-product-vendors";
import { generateInventoryLevelReport } from "./schema/generate-inventory-level-report";
import { getAvailableChannels } from "./schema/get-available-channels";
import { getCatalogUniqueValues } from "./schema/get-catalog-unique-values";
import { getConsignmentCatalog } from "./schema/get-consignment-catalog";
import { getInventoryProducts } from "./schema/get-inventory-products";
import { getLocations } from "./schema/get-locations";
import { getManagedReturnsMetrics } from "./schema/get-managed-returns-metrics";
import { getMarketplaceListings } from "./schema/get-marketplace-listings";
import { getMerchantInventory } from "./schema/get-merchant-inventory";
import { getProductCatalog } from "./schema/get-product-catalog";
import { getProductCategories } from "./schema/get-product-categories";
import { getProductDetails } from "./schema/get-product-details";
import { getProductFamilies } from "./schema/get-product-families";
import { getProductFamilyDetails } from "./schema/get-product-family-details";
import { getProductInventory } from "./schema/get-product-inventory";
import { getProductsForInventory } from "./schema/get-products-for-inventory";
import { getRedoProduct } from "./schema/get-redo-product";
import { getSkuSalesForecast } from "./schema/get-sku-sales-forecast";
import { getSuggestedProductsForLiquidation } from "./schema/get-suggested-products-for-liquidation";
import { getVariantInventoryByLocation } from "./schema/get-variant-inventory-by-location";
import { getWarehouses } from "./schema/get-warehouses";
import { getWarehousesForTeam } from "./schema/get-warehouses-for-team";
import { integrateInventory } from "./schema/integrate-inventory";
import { listConsignedProducts } from "./schema/list-consigned-products";
import { listProducts } from "./schema/list-products";
import { streamListingFieldOptimization } from "./schema/listing-field-optimization/stream-listing-field-optimization";
import { createPricingRule } from "./schema/pricing-rules/create-pricing-rule";
import { deletePricingRule } from "./schema/pricing-rules/delete-pricing-rule";
import { getPricingRules } from "./schema/pricing-rules/get-pricing-rules";
import { updatePricingRule } from "./schema/pricing-rules/update-pricing-rule";
import { getImageOptimizationStatus } from "./schema/product-families/get-image-optimization-status";
import { startImageOptimization } from "./schema/product-families/start-image-optimization";
import { submitImageFeedback } from "./schema/product-families/submit-image-feedback";
import { uploadProductFamilyImages } from "./schema/product-families/upload-product-family-images";
import { publishProductFamilies } from "./schema/publish-product-families";
import { addPurchaseOrderFile } from "./schema/purchase-orders/add-purchase-order-file";
import { createPurchaseOrder } from "./schema/purchase-orders/create-purchase-order";
import { createPurchaseOrderShipment } from "./schema/purchase-orders/create-purchase-order-shipment";
import { createPurchaseOrderViewFolder } from "./schema/purchase-orders/create-purchase-order-view-folder";
import { deletePurchaseOrder } from "./schema/purchase-orders/delete-purchase-order";
import { deletePurchaseOrderFile } from "./schema/purchase-orders/delete-purchase-order-file";
import { deletePurchaseOrderView } from "./schema/purchase-orders/delete-purchase-order-view";
import { deletePurchaseOrderViewFolder } from "./schema/purchase-orders/delete-purchase-order-view-folder";
import { generatePurchaseOrderPdf } from "./schema/purchase-orders/generate-purchase-order-pdf";
import { getPurchaseOrder } from "./schema/purchase-orders/get-purchase-order";
import { getPurchaseOrderViewsAndFolders } from "./schema/purchase-orders/get-purchase-order-views-and-folders";
import { getPurchaseOrders } from "./schema/purchase-orders/get-purchase-orders";
import { getPurchaseOrdersByIds } from "./schema/purchase-orders/get-purchase-orders-by-ids";
import { savePurchaseOrderView } from "./schema/purchase-orders/save-purchase-order-view";
import { updatePurchaseOrder } from "./schema/purchase-orders/update-purchase-order";
import { updatePurchaseOrderViewFolder } from "./schema/purchase-orders/update-purchase-order-view-folder";
import { updatePurchaseOrderViewOrdering } from "./schema/purchase-orders/update-purchase-order-view-ordering";
import { updateShipmentProducts } from "./schema/purchase-orders/update-shipment-products";
import { searchProducts } from "./schema/search-products";
import { addShipmentFile } from "./schema/shipments/add-shipment-file";
import { createShipment } from "./schema/shipments/create-shipment";
import { createShipmentViewFolder } from "./schema/shipments/create-shipment-view-folder";
import { deleteShipment } from "./schema/shipments/delete-shipment";
import { deleteShipmentFile } from "./schema/shipments/delete-shipment-file";
import { deleteShipmentView } from "./schema/shipments/delete-shipment-view";
import { deleteShipmentViewFolder } from "./schema/shipments/delete-shipment-view-folder";
import { getShipment } from "./schema/shipments/get-shipment";
import { getShipmentViewsAndFolders } from "./schema/shipments/get-shipment-views-and-folders";
import { getShipments } from "./schema/shipments/get-shipments";
import { linkShipmentToPurchaseOrder } from "./schema/shipments/link-shipment-to-purchase-order";
import { saveReceivingSession } from "./schema/shipments/save-receiving-session";
import { saveShipmentView } from "./schema/shipments/save-shipment-view";
import { updateShipment } from "./schema/shipments/update-shipment";
import { updateShipmentViewFolder } from "./schema/shipments/update-shipment-view-folder";
import { updateShipmentViewOrdering } from "./schema/shipments/update-shipment-view-ordering";
import { createSupplier } from "./schema/suppliers/create-supplier";
import { getSupplier } from "./schema/suppliers/get-supplier";
import { getSuppliers } from "./schema/suppliers/get-suppliers";
import { updateSupplier } from "./schema/suppliers/update-supplier";
import { unpublishProductFamilies } from "./schema/unpublish-product-families";
import { updateMarketplaceListing } from "./schema/update-marketplace-listing";
import { updateProduct } from "./schema/update-product";
import { updateProductFamily } from "./schema/update-product-family";
import { updateProductFamilyListing } from "./schema/update-product-family-listing";
import { upsertInventoryItem } from "./schema/upsert-inventory-item";

export const rpcDefinition = {
  addPurchaseOrderFile,
  addShipmentFile,
  adjustInventory,
  bulkUpdateForecastingFields,
  cancelAdjustmentSession,
  createAdjustmentSession,
  createInventoryTransfer,
  createMarketplaceListing,
  createPricingRule,
  createPurchaseOrder,
  createPurchaseOrderShipment,
  createPurchaseOrderViewFolder,
  createShipment,
  createSupplier,
  deletePricingRule,
  deleteProduct,
  deletePurchaseOrder,
  deletePurchaseOrderFile,
  deletePurchaseOrderView,
  deleteShipment,
  deleteShipmentFile,
  deletePurchaseOrderViewFolder,
  generateInventoryLevelReport,
  generatePurchaseOrderPdf,
  deleteProductFamilies,
  getAdjustmentSession,
  getAvailableChannels,
  getCatalogUniqueValues,
  getConsignmentCatalog,
  getForecastingProducts,
  getImageOptimizationStatus,
  getInventoryProducts,
  getLocations,
  getProductCategories,
  getManagedReturnsMetrics,
  getMarketplaceListings,
  getMerchantInventory,
  getPricingRules,
  getProductCatalog,
  getProductDetails,
  getProductFamilies,
  getProductFamilyDetails,
  getProductInventory,
  getProductVendors,
  getProductsForInventory,
  getPurchaseOrder,
  getPurchaseOrdersByIds,
  getPurchaseOrderViewsAndFolders,
  getPurchaseOrders,
  getRedoProduct,
  getShipment,
  getShipments,
  getShipmentViewsAndFolders,
  linkShipmentToPurchaseOrder,
  saveReceivingSession,
  saveShipmentView,
  createShipmentViewFolder,
  updateShipmentViewFolder,
  deleteShipmentViewFolder,
  updateShipmentViewOrdering,
  deleteShipmentView,
  getSkuSalesForecast,
  getSuggestedProductsForLiquidation,
  getSupplier,
  getSuppliers,
  getVariantInventoryByLocation,
  getWarehouses,
  getWarehousesForTeam,
  integrateInventory,
  listAdjustmentSessions,
  listConsignedProducts,
  listProducts,
  publishProductFamilies,
  savePurchaseOrderView,
  searchProducts,
  startImageOptimization,
  streamListingFieldOptimization,
  submitAdjustmentSession,
  submitImageFeedback,
  unpublishProductFamilies,
  updateAdjustmentSession,
  updateMarketplaceListing,
  updatePricingRule,
  updateProduct,
  updateProductFamilyListing,
  updateProductFamily,
  updatePurchaseOrder,
  updatePurchaseOrderViewFolder,
  updateShipment,
  updateShipmentProducts,
  updatePurchaseOrderViewOrdering,
  updateSupplier,
  uploadProductFamilyImages,
  upsertInventoryItem,
};

export type RedoInventoryRpcDefinition = InferRpcDefinition<
  typeof rpcDefinition
>;
